import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { X } from "phosphor-react";
import { useEffect, useState } from "react";
import { fget } from "../../../../API/callsAPI";
import useStyles from "./AddACustomPage.style";

const getCreatorName = (createdBy) => {
  if (!createdBy) {
    return "Unknown";
  }

  return [createdBy.first_name, createdBy.last_name].filter(Boolean).join(" ");
};

const mapCustomPage = (page) => ({
  id: page.id,
  name: page.name,
  creator: getCreatorName(page.created_by),
  avatar: page.created_by?.avatar || "",
});

const AddCustomPageModal = ({
  handleClose,
  onSubmit,
  setSnackValue,
  projectId,
  customPages = [],
}) => {
  const classes = useStyles();
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageOptions, setPageOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCustomPages = async () => {
    setIsLoading(true);

    try {
      const response = await fget({
        url: `trackables/custom-page/?project=${projectId}`,
      });
      const results = response.data.results || [];
      const filteredResults = results.filter(
        (page) => !customPages.some((customPage) => customPage.key === page.id),
      );
      setPageOptions(filteredResults.map(mapCustomPage));
    } catch (error) {
      console.error(error);
      setPageOptions([]);
      setSnackValue?.({
        isOpen: true,
        message: "Failed to load custom pages",
        isError: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchCustomPages();
    }
  }, [projectId]);

  const isSubmitDisabled = selectedPages.length === 0 || isSubmitting;

  const handleSubmit = async () => {
    if (selectedPages.length === 0 || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    console.log("selectedPages", selectedPages);

    try {
      const success = await onSubmit?.(selectedPages);
      if (success !== false) {
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={classes.modalContent}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Add a custom page</Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "#B1B5C9" }}
        >
          <X size={28} />
        </IconButton>
      </Box>

      <Typography className={classes.subtitle}>
        This will pin the chosen custom page to the navigation bar
      </Typography>

      <Typography className={classes.label}>
        Choose custom page
        <Box component="span" sx={{ color: "#E41F1F" }}>
          *
        </Box>
      </Typography>

      <Autocomplete
        multiple
        options={pageOptions}
        loading={isLoading}
        value={selectedPages}
        onChange={(_, newValue) => setSelectedPages(newValue)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={
          isLoading ? "Loading custom pages..." : "No custom pages found"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Type and search for custom page"
            variant="outlined"
            size="small"
            className={classes.searchField}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              className={classes.resultItem}
            >
              <Typography className={classes.pageName}>
                {option.name}
              </Typography>

              <Box className={classes.creatorSection}>
                <Typography className={classes.creatorLabel}>
                  Created by
                </Typography>
                <Box className={classes.creatorPill}>
                  <Avatar src={option.avatar} sx={{ width: 22, height: 22 }} />
                  <Typography className={classes.creatorName}>
                    {option.creator}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        }}
        slotProps={{
          listbox: {
            className: classes.resultsList,
          },
          paper: {
            className: classes.resultsPopover,
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#252A38",
            borderRadius: "10px",
            color: "#F9F9F9",
            "& fieldset": { border: "none" },
          },
          "& .MuiChip-root": {
            backgroundColor: "#34394A",
            color: "#F9F9F9",
          },
          "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator":
            {
              color: "#B1B5C9",
            },
        }}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        sx={{
          mt: 3,
          color: "white",
          borderRadius: "10px",
          textTransform: "none",
          fontSize: "14px",
          padding: "10px 22px",
          bgcolor: isSubmitDisabled ? "#252A38" : "#6C65D9",
          "&:hover": {
            bgcolor: isSubmitDisabled ? "#252A38" : "#5a54c7",
          },
          "&.Mui-disabled": { color: "#606479" },
        }}
      >
        {isSubmitting ? "Adding..." : "Add custom page"}
      </Button>
    </Box>
  );
};

export default AddCustomPageModal;
